import { useState, useEffect, useCallback } from 'react';
import { useAuth0Token } from './useAuth0Token';
import {
  getSipsAutomationPreferences,
  updateSipsAutomationPreferences
} from '../services/sipsAutomationService';
import { SipsAutomationPreferences } from '../types/automationPreferences';

/**
 * Hook to fetch SIPS automation preferences for a customer
 * @param customerId - Customer ID to fetch preferences for
 */
export const useSipsAutomationPreferences = (customerId: string | null) => {
  const { token } = useAuth0Token();
  const [preferences, setPreferences] = useState<SipsAutomationPreferences | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = useCallback(async () => {
    if (!token || !customerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await getSipsAutomationPreferences(token, customerId);
      setPreferences(data);
    } catch (err: any) {
      console.error(`Error fetching SIPS automation preferences for ${customerId}:`, err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch automation preferences');
    } finally {
      setLoading(false);
    }
  }, [token, customerId]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const refresh = useCallback(() => {
    return fetchPreferences();
  }, [fetchPreferences]);

  return {
    preferences,
    loading,
    error,
    refresh
  };
};

/**
 * Hook to update SIPS automation preferences
 */
export const useUpdateSipsAutomationPreferences = () => {
  const { token } = useAuth0Token();
  const [updating, setUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (
    customerId: string,
    updates: Partial<SipsAutomationPreferences>
  ): Promise<SipsAutomationPreferences | null> => {
    if (!token) {
      setError('No authentication token');
      return null;
    }

    try {
      setUpdating(true);
      setError(null);

      const updated = await updateSipsAutomationPreferences(token, customerId, updates);
      return updated;
    } catch (err: any) {
      console.error(`Error updating SIPS automation preferences for ${customerId}:`, err);
      setError(err.response?.data?.message || err.message || 'Failed to update automation preferences');
      return null;
    } finally {
      setUpdating(false);
    }
  }, [token]);

  return {
    update,
    updating,
    error
  };
};
